<template>
  <modal :show.sync="show" :on-close="onClose">
    <div class="h-auto overflow-auto flex flex-col">
      <woot-modal-header-v2
        :header-title="$t('AGENT_MGMT.EDIT.TITLE')"
        :header-content="$t('AGENT_MGMT.EDIT.DESC')"
        :custom-classes="'z-[60] border-b border-gray-100'"
        style="box-shadow: 0px 1px 4px #cacaca29"
      >
        <template #titleIcon>
          <span class="material-symbols-outlined text-icon-primary">
            edit
          </span>
        </template>
      </woot-modal-header-v2>

      <form class="form-container flex flex-col w-full items-start !px-6 !py-5">
        <div class="w-full">
          <label
            class="text-gray-700 mb-1.5 leading-5 font-[400]"
            :class="{ error: $v.agentName.$error }"
          >
            {{ $t('AGENT_MGMT.FORM.NAME.LABEL') }}
            <span class="text-green-500">*</span>
          </label>
          <input
            v-model.trim="agentName"
            type="text"
            class="rounded-[8px] border-[#D4D4D4] h-[44px] px-3 py-[10px] hover:border-gray-400 focus:border-gray-400"
            :placeholder="$t('AGENT_MGMT.FORM.NAME.PLACEHOLDER')"
            @input="$v.agentName.$touch"
          />
        </div>
        <div class="w-full mb-6">
          <label class="text-gray-700 mb-1.5 leading-5 font-[400]">
            {{ $t('AGENT_MGMT.FORM.EMAIL.LABEL') }}
            <span class="text-green-500">*</span>
          </label>
          <div class="relative">
            <input
              v-model.trim="agentEmail1"
              type="text"
              class="rounded-[8px] border-[#D4D4D4] h-[44px] px-3 py-[10px] mb-1.5 hover:border-gray-400 focus:border-gray-400 read-only:bg-[#F5F5F5]"
              placeholder="example@email.com"
              readonly="true"
            />
            <span
              class="material-symbols-outlined absolute top-0 bottom-0 right-3 rtl:right-[unset] rtl:left-3 text-[20px] m-auto h-[19px] text-gray-400 cursor-not-allowed"
            >
              lock
            </span>
          </div>
          <div class="text-sm text-gray-700 mt-1.5">
            {{ $t('AGENT_MGMT.FORM.EMAIL.EDIT_DESC') }}
          </div>
        </div>
        <hr class="border-gray-200 w-full m-0" />
        <div class="w-full mt-6">
          <label
            :class="{ error: $v.agentType.$error }"
            class="text-gray-700 block mb-1.5 leading-5 font-[400]"
          >
            {{ $t('AGENT_MGMT.FORM.AGENT_TYPE.LABEL') }}
            <span class="text-green-500">*</span>
          </label>
          <woot-multiselect
            v-model="agentTypeSelected"
            track-by="name"
            label="label"
            selected-label
            deselect-label=""
            :options="fullRoles"
            :allow-empty="false"
            :show-labels="false"
            class="no-margin"
            :placeholder="$t('AGENT_MGMT.FORM.AGENT_TYPE.PLACEHOLDER')"
          />
          <span
            v-if="$v.agentType.$error"
            class="text-[#B91C1C] text-sm message"
          >
            {{ $t('AGENT_MGMT.FORM.AGENT_TYPE.ERROR') }}
          </span>
          <div class="text-sm text-gray-700 mt-1.5">
            {{ $t('AGENT_MGMT.FORM.AGENT_TYPE.DESC') }}
          </div>
        </div>
        <div v-if="agentType === 'Agent'" class="w-full mt-3">
          <label class="text-gray-700 flex flex-row items-center font-[400]">
            <input
              v-model="agentSeeAllConversations"
              type="checkbox"
              class="semantic-checkbox"
            />
            {{ $t('AGENT_MGMT.FORM.SEE_ALL_CONVERSATIONS.LABEL') }}
          </label>
        </div>
        <div class="w-full relative mt-4">
          <label
            :class="{ error: $v.agentAvailability.$error }"
            class="font-[400] mb-1.5 text-gray-700"
          >
            {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          </label>
          <woot-multiselect
            v-model="onAgentAvailabilityChange"
            track-by="value"
            label="label"
            selected-label
            deselect-label=""
            :options="availabilityStatuses"
            :allow-empty="false"
            :show-labels="false"
            class="no-margin"
            :placeholder="
              $t('AGENT_MGMT.FORM.AGENT_TWO_FACTOR_AUTH.PLACEHOLDER')
            "
          />
          <span
            v-if="$v.agentAvailability.$error"
            class="text-[#B91C1C] text-sm message"
          >
            {{ $t('AGENT_MGMT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </div>

        <!-- QM §4.6, §5.1.2 — the per-agent cap the admin persona configures. Only
             meaningful while queue management runs the routing. -->
        <div v-if="isQueueManagementEnabled" class="w-full relative mt-4">
          <label
            class="font-[400] mb-1.5 text-gray-700"
            :class="{ error: $v.agentCapacityCap.$error }"
          >
            {{ $t('AGENT_MGMT.FORM.CAPACITY_CAP.LABEL') }}
          </label>
          <input
            v-model.trim="agentCapacityCap"
            type="number"
            min="1"
            step="1"
            class="rounded-[8px] border-[#D4D4D4] h-[44px] px-3 py-[10px] hover:border-gray-400 focus:border-gray-400"
            :placeholder="$t('AGENT_MGMT.FORM.CAPACITY_CAP.PLACEHOLDER')"
            @input="$v.agentCapacityCap.$touch"
          />
          <span
            v-if="$v.agentCapacityCap.$error"
            class="text-[#B91C1C] text-sm message"
          >
            {{ $t('AGENT_MGMT.FORM.CAPACITY_CAP.ERROR') }}
          </span>
          <div class="text-sm text-gray-700 mt-1.5">
            {{ $t('AGENT_MGMT.FORM.CAPACITY_CAP.DESC') }}
          </div>
        </div>

        <div class="w-full relative mt-4">
          <label class="font-[400] mb-1.5 text-gray-700">
            {{ $t('AGENT_MGMT.FORM.AGENT_SKILLS.LABEL') }}
          </label>
          <custom-attribute-selector
            attribute-type="agent_attribute"
            :init-custom-attributes="selectedCustomAttributes"
            @addAttribute="addAttribute"
            @removeAttribute="removeAttribute"
          />
          <div class="text-sm font-[400] text-gray-700 mt-1.5 mb-3">
            {{ $t('AGENT_MGMT.FORM.AGENT_SKILLS.DESC') }}
          </div>
          <custom-attributes
            attribute-type="agent_attribute"
            attribute-class="conversation--attribute"
            :init-custom-attributes="selectedCustomAttributes"
            class="even"
            @changeAttributes="changeAttributes"
          />
        </div>

        <hr class="w-full m-0 my-6 p-0 border-gray-100" />

        <div class="w-full">
          <label
            class="font-[400] mb-1.5 text-gray-700"
            :class="{ error: $v.agentTwoFactorType.$error }"
          >
            {{ $t('AGENT_MGMT.FORM.2FA.TITLE') }}
            <span v-if="isMandatory2fa" class="text-green-500">*</span>
          </label>
          <woot-multiselect
            v-model="onAgentTwoFactorTypeChange"
            label="name"
            selected-label=""
            deselect-label=""
            :options="twoFAoptions"
            :allow-empty="true"
            :show-labels="false"
            class="no-margin"
            :placeholder="
              $t('AGENT_MGMT.FORM.AGENT_TWO_FACTOR_AUTH.PLACEHOLDER')
            "
          />
          <div class="text-sm font-[400] text-gray-700 mt-1.5">
            {{ $t('AGENT_MGMT.FORM.2FA.DESC') }}
          </div>
        </div>
        <div v-show="agentTwoFactorType" class="mt-4 w-full">
          <div v-show="agentTwoFactorType === 'sms'">
            <div class="w-full">
              <label
                class="font-[400] mb-1.5 text-gray-700"
                :class="{ 'text-[#B91C1C]': $v.agentPhone1.$error }"
              >
                {{ $t('AGENT_MGMT.FORM.AUTHENTICATION_PHONE.LABEL_PHONE1') }}
                <span class="text-green-500">*</span>
              </label>
              <woot-phone-input
                v-model="agentPhone1"
                placeholder="XXX-XXXXXX"
                :has-error-in-phone-input="
                  $v.agentPhone1.$error ||
                  !$v.agentTwoFactorType.emptyPhone ||
                  $v.agentPhone1.$invalid
                "
                :show-flag-in-selection="true"
                :dropdown-direction="'top'"
              />
              <span v-if="$v.agentPhone1.$error" class="text-[#B91C1C] text-sm">
                {{ $t('AGENT_MGMT.FORM.AUTHENTICATION_PHONE.ERROR.FORMAT') }}
              </span>
              <div
                v-if="!$v.agentTwoFactorType.emptyPhone"
                class="text-[#B91C1C] text-sm"
              >
                <span class="message">
                  {{
                    $t('AGENT_MGMT.FORM.AUTHENTICATION_PHONE.ERROR.EMPTY_PHONE')
                  }}
                </span>
              </div>
            </div>
          </div>
          <div v-show="agentTwoFactorType === 'email'" class="w-full">
            <div class="w-full">
              <label
                class="font-[400] mb-1.5 text-gray-700"
                :class="{ 'text-[#B91C1C]': $v.agentEmail1.$error }"
              >
                {{ $t('AGENT_MGMT.FORM.AUTHENTICATION_EMAIL.LABEL') }}
                <span class="text-green-500">*</span>
              </label>
              <input
                v-model="agentEmail1"
                type="email"
                placeholder="example@email.com"
                class="rounded-[8px] border h-[44px] px-3 py-[10px] mb-1.5"
                :class="
                  $v.agentEmail1.$invalid
                    ? 'border-[#B91C1C] hover:border-[#B91C1C] focus:border-[#B91C1C]'
                    : 'border-gray-300 hover:border-gray-400 focus:border-gray-400'
                "
                @input="$v.agentEmail1.$touch"
              />
              <div
                v-if="!$v.agentTwoFactorType.emptyEmail"
                class="text-[#B91C1C] text-sm"
              >
                {{
                  $t('AGENT_MGMT.FORM.AUTHENTICATION_EMAIL.ERROR.EMPTY_EMAIL')
                }}
              </div>
              <div
                v-if="$v.agentEmail1.$invalid"
                class="text-[#B91C1C] text-sm"
              >
                {{
                  $t('AGENT_MGMT.FORM.AUTHENTICATION_EMAIL.ERROR.EMAIL_FORMAT')
                }}
              </div>
              <div v-else class="text-sm font-[400] text-gray-700 mt-1.5">
                {{ $t('AGENT_MGMT.FORM.AUTHENTICATION_EMAIL.DESC') }}
              </div>
            </div>
          </div>
        </div>
        <div class="w-full mt-6">
          <div
            class="button-outline w-full py-3 text-base text-center flex flex-row items-center justify-center gap-2"
            @click.prevent="resetPassword"
          >
            <span class="material-symbols-outlined text-green-500">
              key_vertical
            </span>
            {{ $t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON') }}
          </div>
          <div class="mt-[6px] text-sm text-gray-600">
            {{ $t('AGENT_MGMT.EDIT.PASSWORD_RESET.DESC') }}
          </div>
        </div>
      </form>

      <div
        class="flex flex-row justify-end gap-2 pt-4 pb-6 px-6 w-full z-[60] border-t border-gray-100"
        style="box-shadow: 0px -1px 4px #cacaca29"
      >
        <woot-button
          class="cancel-btn button clear text-base px-5 py-[11px] leading-[20px] h-[48px] font-[400]"
          color-scheme="secondary"
          @click.prevent="onClose"
        >
          {{ $t('AGENT_MGMT.FORM.CANCEL_BUTTON_TEXT') }}
        </woot-button>
        <woot-submit-button
          :disabled="
            $v.agentType.$invalid ||
            $v.agentName.$invalid ||
            $v.agentTwoFactorType.$invalid ||
            $v.agentPhone1.$invalid ||
            uiFlags.isUpdating
          "
          :button-text="$t('AGENT_MGMT.FORM.SUBMIT')"
          :loading="uiFlags.isUpdating"
          class="button-filled text-base px-5 py-2.5 font-[400] h-[unset]"
          @click.native.prevent="editAgent"
        />
      </div>
    </div>
  </modal>
</template>

<script>
import { required, minLength, helpers } from 'vuelidate/lib/validators';
const strictEmail = helpers.regex('strictEmail', /^[^\s@]+@[^\s@]+\.[^\s@]+$/);
import { mapGetters } from 'vuex';
import WootSubmitButton from '../../../../components/buttons/FormSubmitButton.vue';
import Modal from '../../../../components/Modal.vue';
import Auth from '../../../../api/auth';
import wootConstants from 'dashboard/constants/globals';
import { isPhoneIsraelEmpty } from 'shared/helpers/Validators';
import CustomAttributes from './CustomAttributes.vue';
import CustomAttributeSelector from './CustomAttributesSelector.vue';
// QM §4.6 — reused purely for its `isQueueManagementEnabled` computed.
import queueSupervisionMixin from 'dashboard/mixins/queueSupervision';

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

export default {
  components: {
    WootSubmitButton,
    Modal,
    CustomAttributes,
    CustomAttributeSelector,
  },
  mixins: [queueSupervisionMixin],
  props: {
    id: {
      type: Number,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    email: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: '',
    },
    availability: {
      type: String,
      default: '',
    },
    seeAllConversations: {
      type: Boolean,
      default: undefined,
    },
    onClose: {
      type: Function,
      required: true,
    },
    twoFactorType: {
      type: String,
      default: '',
    },
    phone1: {
      type: String,
      default: '',
    },
    email1: {
      type: String,
      default: '',
    },
    agentCustomAttributes: {
      type: Object,
      default: () => ({}),
    },
    // QM §4.6 — null/absent means no per-agent override (account default, else unlimited).
    capacityCap: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      roles: [
        {
          name: 'administrator',
          label: this.$t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
        },
        {
          name: 'Agent',
          label: this.$t('AGENT_MGMT.AGENT_TYPES.AGENT'),
        },
      ],
      agentName: this.name,
      agentAvailability: this.availability,
      agentType: this.type,
      agentSeeAllConversations: this.seeAllConversations,
      agentCredentials: {
        email: this.email,
      },
      show: true,
      agentPhone1: this.phone1,
      agentEmail1: this.email1,
      agentTwoFactorType: this.twoFactorType,
      selectedCustomAttributes: this.agentCustomAttributes,
      agentCapacityCap:
        this.capacityCap === null ? '' : String(this.capacityCap),
    };
  },
  validations: {
    agentName: {
      required,
      minLength: minLength(1),
    },
    agentType: {
      required,
    },
    agentAvailability: {
      required,
    },
    agentPhone1: {
      isPhoneIsraelEmpty,
    },
    agentEmail1: {
      required,
      email: strictEmail,
    },
    // QM §4.6 — empty is valid and means "no limit"; anything else must be a whole number > 0.
    agentCapacityCap: {
      positiveIntegerOrBlank(value) {
        return (
          value === '' || value === null || /^[1-9]\d*$/.test(String(value))
        );
      },
    },
    agentTwoFactorType: {
      emptyPhone: function (value) {
        return value !== 'sms' || !!this.agentPhone1;
      },
      emptyEmail: function (value) {
        return value !== 'email' || !!this.agentEmail1;
      },
    },
  },
  computed: {
    ...mapGetters({
      uiFlags: 'agents/getUIFlags',
      getAccount: 'accounts/getAccount', // d99d
      accountId: 'getCurrentAccountId', // d99d
      rolesFromStore: 'roles/getRoles',
      loggedInUser: 'getCurrentUser',
    }),
    // d99d
    account() {
      return this.getAccount(this.accountId);
    },
    isMandatory2fa() {
      return this.account?.mandatory_2fa;
    },
    newRulesSystemEnabled() {
      return this.account?.features?.new_rules_system;
    },
    fullRoles() {
      if (!this.newRulesSystemEnabled) {
        return this.roles;
      }
      return this.rolesFromStore.map(role => ({
        name: role.name,
        label: this.$t(`ROLES.NAMES.${role.name}`),
      }));
    },
    // .
    availabilityStatuses() {
      return this.$t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUSES_LIST').map(
        (statusLabel, index) => ({
          label: statusLabel,
          value: AVAILABILITY_STATUS_KEYS[index],
          disabled:
            this.currentUserAvailability === AVAILABILITY_STATUS_KEYS[index],
        })
      );
    },
    twoFAoptions() {
      return [
        {
          name: this.$t('AGENT_MGMT.FORM.AGENT_TWO_FACTOR_AUTH.OFF'),
          value: '',
          $isDisabled: this.isMandatory2fa,
        },
        {
          name: this.$t('AGENT_MGMT.FORM.AGENT_TWO_FACTOR_AUTH.sms'),
          value: 'sms',
        },
        {
          name: this.$t('AGENT_MGMT.FORM.AGENT_TWO_FACTOR_AUTH.email'),
          value: 'email',
        },
      ];
    },
    agentTypeSelected: {
      get() {
        const selected = this.fullRoles.find(
          opt => opt.name === this.agentType
        );
        return selected;
      },
      set(option) {
        this.agentType = option.name;
      },
    },
    onAgentAvailabilityChange: {
      get() {
        return this.availabilityStatuses.find(
          i => i.value === this.agentAvailability.value
        );
      },
      set(opt) {
        this.agentAvailability = opt.value;
      },
    },
    onAgentTwoFactorTypeChange: {
      get() {
        if (!this.agentTwoFactorType) return this.twoFAoptions[0];
        return this.twoFAoptions.find(i => i.value === this.agentTwoFactorType);
      },
      set(option) {
        this.agentTwoFactorType = option.value;
      },
    },
  },
  watch: {
    agentTwoFactorType(newVal) {
      if (newVal === 'email' && !this.agentEmail1) {
        this.agentEmail1 = this.email;
      }
    },
  },
  mounted() {
    if (this.newRulesSystemEnabled) {
      this.$store.dispatch('roles/get');
    }
  },
  methods: {
    showAlert(message) {
      bus.$emit('newToastMessage', message);
    },
    async editAgent() {
      if (this.agentType === 'administrator') {
        this.agentSeeAllConversations = undefined;
      }
      try {
        await this.$store.dispatch('agents/update', {
          id: this.id,
          name: this.agentName,
          role: this.agentType,
          see_all_conversations: this.agentSeeAllConversations,
          availability: this.agentAvailability,
          custom_attributes: this.selectedCustomAttributes,
          two_factor_type: this.agentTwoFactorType,
          phone1: this.agentPhone1, // d99d
          email1: this.agentEmail1, // d99d
          // QM §4.6 — sent as '' to clear the cap back to unlimited.
          capacity_cap: this.agentCapacityCap,
        });
        // QM §5.4.1 — the profile capacity indicator renders from the cached current user.
        if (this.id === this.loggedInUser?.id) {
          await this.$store.dispatch('validityCheck');
        }
        this.showAlert(this.$t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
        this.onClose();
      } catch (error) {
        this.showAlert(this.$t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    async resetPassword() {
      try {
        await Auth.resetPassword(this.agentCredentials);
        this.showAlert(
          this.$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE')
        );
      } catch (error) {
        this.showAlert(this.$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
      }
    },
    addAttribute(attributeKey, attributeValue) {
      this.$set(this.selectedCustomAttributes, attributeKey, attributeValue);
    },
    removeAttribute(attributeKey) {
      const { [attributeKey]: _, ...rest } = this.selectedCustomAttributes;
      this.selectedCustomAttributes = rest;
    },
    changeAttributes(customAttrs) {
      this.selectedCustomAttributes = customAttrs;
    },
  },
};
</script>
<style lang="scss" scoped>
.form-container {
  max-height: 500px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    @apply bg-gray-300 rounded-full;
  }

  &::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-400;
  }
}
</style>
<style lang="scss">
.edit-agent-modal {
  .modal-container {
    @apply rounded-[12px];
  }
}
</style>
